import { useEffect, type ReactNode } from 'react'

export const card =
  'rounded-2xl bg-white dark:bg-[#1a1425] shadow-sm border border-gray-100 dark:border-[#2d2236]'
export const inputClass =
  'w-full rounded-xl border border-gray-300 dark:border-[#3d2c4f] bg-white dark:bg-[#1a1520] px-3 py-2 text-sm text-gray-900 dark:text-white focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-60'
export const labelClass = 'mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300'
export const hintClass = 'mt-1 text-xs text-gray-500 dark:text-gray-400'
export const sectionTitle = 'font-heading text-lg font-semibold text-gray-900 dark:text-white'
export const tableWrap =
  'overflow-x-auto rounded-2xl border border-[#ecdefc] dark:border-[#3d2c4f] bg-white dark:bg-[#1a1520]'
export const theadClass =
  'bg-[#faf6ff] dark:bg-[#221728] text-left text-xs uppercase tracking-wider text-[#7f4f9a] dark:text-[#b58fcc]'
export const rowClass = 'border-t border-[#f0e3ff] dark:border-[#2d2236]'
export const clickableRow = `${rowClass} cursor-pointer hover:bg-[#faf6ff] dark:hover:bg-[#2d2236]`

const TONES: Record<string, string> = {
  green: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  amber: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  red: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
  blue: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200',
  purple: 'bg-[#f0e3ff] text-[#503864] dark:bg-[#3d2c4f] dark:text-[#e2ccf5]',
  gray: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
}

const STATUS_TONE: Record<string, keyof typeof TONES> = {
  pending: 'amber',
  approved: 'blue',
  paid: 'green',
  rejected: 'red',
  void: 'gray',
  draft: 'gray',
  active: 'green',
  paused: 'amber',
  closed: 'gray',
  applications_open: 'green',
  applications_closed: 'blue',
  day_of: 'purple',
  wrapped: 'gray',
}

export function Badge({ children, tone }: { children: ReactNode; tone: keyof typeof TONES }) {
  return (
    <span
      className={`inline-flex whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ${TONES[tone]}`}
    >
      {children}
    </span>
  )
}

export function StatusBadge({ status }: { status: string }) {
  return <Badge tone={STATUS_TONE[status] || 'gray'}>{status.replace(/_/g, ' ')}</Badge>
}

export function Stat({
  label,
  value,
  hint,
}: {
  label: string
  value: ReactNode
  hint?: ReactNode
}) {
  return (
    <div className={`${card} p-4`}>
      <div className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">
        {label}
      </div>
      <div className="mt-1 font-heading text-2xl font-bold text-gray-900 dark:text-white">
        {value}
      </div>
      {hint && <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">{hint}</div>}
    </div>
  )
}

export function Spinner() {
  return (
    <div className="flex justify-center py-12" role="status" aria-label="Loading">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  )
}

export function ErrorBanner({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div
      role="alert"
      className="mb-4 flex items-center justify-between gap-4 rounded-xl bg-red-50 p-4 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-200"
    >
      <span>{message}</span>
      {onRetry && (
        <button type="button" onClick={onRetry} className="font-medium underline">
          Retry
        </button>
      )}
    </div>
  )
}

export function Notice({
  children,
  tone = 'blue',
}: {
  children: ReactNode
  tone?: 'blue' | 'amber' | 'green'
}) {
  const tones = {
    blue: 'bg-sky-50 text-sky-800 dark:bg-sky-900/20 dark:text-sky-200',
    amber: 'bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:text-amber-200',
    green: 'bg-emerald-50 text-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-200',
  }
  return <div className={`rounded-xl px-4 py-3 text-sm ${tones[tone]}`}>{children}</div>
}

export function Empty({ children }: { children: ReactNode }) {
  return (
    <div className="py-12 text-center text-sm text-gray-500 dark:text-gray-400">{children}</div>
  )
}

export function Modal({
  title,
  onClose,
  children,
  wide,
}: {
  title: string
  onClose: () => void
  children: ReactNode
  wide?: boolean
}) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(event) => event.stopPropagation()}
        className={`max-h-[90vh] w-full overflow-y-auto rounded-2xl bg-white p-6 dark:bg-[#1a1520] ${wide ? 'max-w-3xl' : 'max-w-lg'}`}
      >
        <div className="mb-4 flex items-start justify-between gap-4">
          <h3 className="font-heading text-xl font-bold text-gray-900 dark:text-white">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-1 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-[#2d2236]"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        {children}
      </div>
    </div>
  )
}

export function Pager({
  offset,
  limit,
  total,
  onChange,
}: {
  offset: number
  limit: number
  total: number
  onChange: (offset: number) => void
}) {
  if (total <= limit && offset === 0) return null
  const page = Math.floor(offset / limit) + 1
  const pages = Math.ceil(total / limit)
  const button =
    'rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-40 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700'
  return (
    <div className="mt-4 flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
      <span>
        Page {page} of {pages} · {total} total
      </span>
      <div className="flex gap-2">
        <button
          type="button"
          className={button}
          disabled={page <= 1}
          onClick={() => onChange(offset - limit)}
        >
          Previous
        </button>
        <button
          type="button"
          className={button}
          disabled={page >= pages}
          onClick={() => onChange(offset + limit)}
        >
          Next
        </button>
      </div>
    </div>
  )
}
